/* eslint-disable @typescript-eslint/no-explicit-any */
import Tracker from '@openreplay/tracker';
import {
  paginaComCredencial,
  sanitizarRequisicao,
  sanitizarUrlDaPagina,
} from '../utils/openreplay-sanitizer';

class OpenReplayService {
  private tracker: Tracker | null = null;
  private isInitialized = false;
  // Revisão do #665: depois de uma página de link nesta aba, o tracker não
  // volta. O Viewport do tracker guarda a URL anterior CRUA e a manda como
  // referrer na próxima troca de página, sem passar pelo `urlSanitizer` (não há
  // opção para isso na 16.4.7). Sem reiniciar, esse referrer nunca sai.
  private bloqueado = false;

  initialize() {
    if (this.isInitialized || this.bloqueado) return;
    // Mesma razão: o Viewport começa com `document.referrer` cru. Página
    // carregada vindo de um link (ex.: `/pix/<pedido>` → `/`) fica sem sessão.
    if (paginaComCredencial(window.location.pathname) || paginaComCredencial(document.referrer)) {
      this.bloqueado = true;
      return;
    }

    try {
      this.tracker = new Tracker({
        projectKey: 'rlnUqn3CDT1J6ZB9oEV5',
        // Configurações básicas
        respectDoNotTrack: true, // Respeita a configuração "Do Not Track" do navegador
        // #665: credencial de link fora da URL de requisição e de página
        // (os outros campos são os padrões do tracker; o tipo exige todos)
        network: {
          failuresOnly: false,
          capturePayload: false,
          ignoreHeaders: ['cookie', 'set-cookie', 'authorization'],
          sessionTokenHeader: false,
          captureInIframes: true,
          sanitizer: sanitizarRequisicao,
        },
        urls: {
          urlSanitizer: sanitizarUrlDaPagina,
        },
        // Timing grava o nome de todo recurso (XHR/fetch inclusive) com
        // `buffered: true`: pega até as chamadas de antes do start
        resourceNameSanitizer: sanitizarUrlDaPagina,
      });

      this.tracker.start();
      this.isInitialized = true;
      console.log('OpenReplay initialized successfully');
    } catch (error) {
      console.error('Failed to initialize OpenReplay:', error);
    }
  }

  // Identificar usuário por ID
  setUserID(userId: string) {
    if (this.tracker) {
      this.tracker.setUserID(userId);
      console.log('OpenReplay: User ID set to', userId);
    }
  }

  // Adicionar metadados do usuário
  setMetadata(key: string, value: string) {
    if (this.tracker) {
      this.tracker.setMetadata(key, value);
      console.log('OpenReplay: Metadata set', key, '=', value);
    }
  }

  // Adicionar múltiplos metadados de uma vez
  setUserMetadata(userData: {
    name?: string;
    email?: string;
    phone?: string;
    plan?: string;
    step?: string;
    coupon?: string;
    paymentMethod?: string;
    [key: string]: string | undefined;
  }) {
    if (this.tracker) {
      Object.entries(userData).forEach(([key, value]) => {
        if (value) {
          this.tracker!.setMetadata(key, value);
        }
      });
      console.log('OpenReplay: User metadata set', userData);
    }
  }

  // Capturar evento customizado
  trackEvent(eventName: string, data?: any) {
    if (this.tracker) {
      this.tracker.event(eventName, data);
      console.log('OpenReplay: Event tracked', eventName, data);
    }
  }

  /**
   * Chamado a cada troca de rota (`main.tsx`), antes dos efeitos da página
   * nova. Página de link (`/pix/<pedido>`, `/renovar/<token>`…): para o tracker
   * se estiver gravando (DOM, console, rede) e não deixa iniciar de novo.
   */
  aoTrocarDeRota(caminho: string) {
    if (!paginaComCredencial(caminho)) return;
    this.bloqueado = true;
    this.stop();
  }

  // Parar o tracker
  stop() {
    if (this.tracker) {
      this.tracker.stop();
      this.isInitialized = false;
      console.log('OpenReplay stopped');
    }
  }

  // Verificar se está inicializado
  isReady() {
    return this.isInitialized && this.tracker !== null;
  }
}

// Exportar uma instância singleton
export const openReplayService = new OpenReplayService();
